import customFetch from '../../utils/customFetch'
import { realEstateToApp } from './converters/toApp/realEstate'
import { API_ENDPOINT } from '../../config'
import { ConvertedRealEstate, RealEstate, RealEstateDTO } from '../../@types/real-estate-api-types'
import { parseJSON, safeParseJSON } from '../../utils/apiHelper/apiHelper'
import { ContentStateInterface } from '../../@types/types'
import { isWebView } from '../../utils/navigation'
import {
  AI_TEXT_LISTING_SESSION_ID_HEADER,
  AI_TEXT_REQUEST_ID_HEADER,
} from '../../redux/thunks/suggestedText/suggestedTextLogging'

const offerApiUrl = API_ENDPOINT + '/anbieten/private-anbieter/inserieren/api/realEstate/offer-api'

export type RealEstateValidationIssue = {
  fieldName: string
  message: string
}

const options = {
  method: 'GET',
  headers: {
    Accept: 'application/json; charset=utf-8',
    'Content-Type': 'application/json; charset=UTF-8',
  },
  credentials: 'include',
}

export const saveAndGetRealEstateListing = async (
  uuid: string,
  realEstateObject: RealEstateDTO,
  investigationContext?: {
    // Correlates save/reload calls with the AI suggestion request that last won.
    requestId?: string
    // Correlates multiple actions happening during one listing editing session.
    listingSessionId?: string
  }
): Promise<RealEstate> => {
  const parameters = new URLSearchParams()
  parameters.append('updateObject', 'true')
  parameters.append('requestSource', 'clf')
  if (isWebView()) {
    parameters.append('isMobileView', 'true')
  }

  const options = {
    method: 'PUT',
    headers: {
      Accept: 'application/json; charset=utf-8',
      'Content-Type': 'application/json; charset=UTF-8',
      ...(investigationContext?.requestId ? { [AI_TEXT_REQUEST_ID_HEADER]: investigationContext.requestId } : {}),
      ...(investigationContext?.listingSessionId
        ? { [AI_TEXT_LISTING_SESSION_ID_HEADER]: investigationContext.listingSessionId }
        : {}),
    },
    credentials: 'include',
    body: JSON.stringify(realEstateObject),
  }

  const response = await customFetch(offerApiUrl + `/${uuid}\?${parameters.toString()}`, options)
  if (!response.ok) {
    const text = await response.text()
    const errorPayload = safeParseJSON(text)
    const errorDetail = errorPayload?.detail ?? errorPayload?.details
    const errorMessage = `Failed to save listing id:${realEstateObject.estate.id},category:${realEstateObject.estate.category},transaction:${realEstateObject.estate.transaction} with status ${response.status} and response ${text}`

    if (response.status === 400) {
      const validationIssues: RealEstateValidationIssue[] = Array.isArray(errorPayload)
        ? errorPayload.filter(
            (issue): issue is RealEstateValidationIssue =>
              typeof issue?.fieldName === 'string' && typeof issue?.message === 'string'
          )
        : []
      throw new RealEstateValidationError(errorMessage, validationIssues)
    } else if (response.status === 403) {
      throw new AccessForbiddenError(errorMessage)
    } else if (response.status === 404 && errorDetail === 'REAL_ESTATE_NOT_FOUND') {
      throw new RealEstateNotFoundError(errorMessage)
    } else if (response.status === 404 && errorDetail === 'CLB_REAL_ESTATE_NOT_FOUND') {
      throw new ClbRealEstateNotFoundError(errorMessage)
    } else {
      throw new Error(errorMessage)
    }
  }
  return response.json()
}

export const getEstateByCLBUuid: (
  uuid: string,
  defaultStateValues: ContentStateInterface,
  investigationContext?: {
    // Correlates save/reload calls with the AI suggestion request that last won.
    requestId?: string
    // Correlates multiple actions happening during one listing editing session.
    listingSessionId?: string
  }
) => Promise<ConvertedRealEstate | null> = async (uuid, defaultStateValues, investigationContext) => {
  const requestOptions = {
    ...options,
    headers: {
      ...options.headers,
      ...(investigationContext?.requestId ? { [AI_TEXT_REQUEST_ID_HEADER]: investigationContext.requestId } : {}),
      ...(investigationContext?.listingSessionId
        ? { [AI_TEXT_LISTING_SESSION_ID_HEADER]: investigationContext.listingSessionId }
        : {}),
    },
  }
  const response = await customFetch(
    `${API_ENDPOINT}/anbieten/private-anbieter/inserieren/api/realEstate/${uuid}`,
    requestOptions
  )

  if (response.status === 404) {
    const text = await response.text()
    const errorPayload = safeParseJSON(text)
    const errorDetail = errorPayload?.detail ?? errorPayload?.details

    if (errorDetail === 'REAL_ESTATE_NOT_FOUND') {
      throw new RealEstateNotFoundError(`Listing ${uuid} has been deleted`)
    }

    return null
  }

  const result: RealEstate | null = await parseJSON(response)
  return result ? realEstateToApp(result, defaultStateValues) : null
}

export const checkIfOtherExternalIdExist: (externalId: string, hofRealEstateId: string) => Promise<boolean> = async (
  externalId,
  hofRealEstateId
) => {
  return await customFetch(
    `${API_ENDPOINT}/anbieten/private-anbieter/inserieren/api/realEstate/externalId/${externalId}/exist?hofRealEstateId=${hofRealEstateId}`,
    options
  ).then((r) => r.json())
}

export class AccessForbiddenError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AccessForbiddenError'
    Object.setPrototypeOf(this, AccessForbiddenError.prototype)
  }
}

export class RealEstateValidationError extends Error {
  readonly validationIssues: RealEstateValidationIssue[]

  constructor(message: string, validationIssues: RealEstateValidationIssue[]) {
    super(message)
    this.name = 'RealEstateValidationError'
    this.validationIssues = validationIssues
    Object.setPrototypeOf(this, RealEstateValidationError.prototype)
  }
}

export class RealEstateNotFoundError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'RealEstateNotFoundError'
    Object.setPrototypeOf(this, RealEstateNotFoundError.prototype)
  }
}

export class ClbRealEstateNotFoundError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ClbRealEstateNotFoundError'
    Object.setPrototypeOf(this, ClbRealEstateNotFoundError.prototype)
  }
}
